import React, {useEffect, useState} from 'react';

import {
  Breakpoints,
  ButtonAppearance,
  ButtonCustom,
  SupDisclaimer,
  TextAppearance,
  TextColor,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextTag,
  TextWeight,
} from '@vw-marketing/us-components';

import {
  AppConstants,
  DEFAULT_CURRENCY_MAP,
  DEFAULT_WARRANTY,
  ID4_WARRANTY,
} from '../../utils/app-constants';
import {BuildTheDeal} from '../icons/BuildTheDeal';
import {
  dealerDistanceTextStyle,
  dealerNameTextStyle,
  dealerPhoneTextStyle,
  modelMileageTextStyle,
  modelNameTextStyle,
  modelPriceTextStyle,
  modelTrimNameTextStyle,
  modelWarrantyTextStyle,
} from './helpers';
import {isOnlineBuyingLinkModified} from '../../utils/general';
import {
  StyledBlackContainer,
  StyledOfferTypeLabel,
  StyledOfferTypeLabelWrapper,
  StyledVehicleCardWrapper,
  StyledVehicleContentContainer,
  StyledVehicleContentWrapper,
  StyledVehicleDealerWrapper,
  StyledVehicleImageWrapper,
} from './styles';
import {UsedVehicle} from '../../typing/used-inventory';
import {useFeatureAppConfig} from '../../hooks/use-feature-app-config';
import {useFeatureServices} from '../../hooks/use-feature-services';
import {useTranslations} from '../../hooks/use-translations';
import ClickablePhone from '../clickable-phone';
interface SearchResultsVehicleCardProps {
  vehicle: UsedVehicle;
  onHandleVehicleClick: React.MouseEventHandler<HTMLDivElement>;
  priceDisclaimer?: string;
  warrantyDisclaimer?: string;
  startBuyingHandler: (name: string) => void;
}

const SearchResultsVehicleCard = ({
  vehicle,
  onHandleVehicleClick,
  warrantyDisclaimer,
  priceDisclaimer,
  startBuyingHandler,
}: SearchResultsVehicleCardProps) => {
  const localeService = useFeatureServices()['locale-service'];
  const {
    currencyMap = DEFAULT_CURRENCY_MAP,
    warrantyMonths = DEFAULT_WARRANTY,
  } = useFeatureAppConfig();
  const {dealer, resultsSection, buildMyDeal} = useTranslations();
  const vehiclePrice = Number.parseInt(vehicle?.adjustedListPrice || '');
  const noFoundImage = AppConstants.NoVehicleImage;
  const isID4: boolean = vehicle.model?.trim() === 'ID.4';
  /**
   * Issue reported NGW-4673
   * Prevent slider carousel bug when user clicks on disclaimer
   * @param e: Event
   */
  const onAbortEventReplication: React.MouseEventHandler<HTMLDivElement> = e => {
    e?.nativeEvent.preventDefault();
    e?.nativeEvent?.stopImmediatePropagation();
    e?.preventDefault();
    e?.stopPropagation();
  };

  const [validImageUrl, setValidImageUrl] = useState<string>(
    vehicle?.mediaImageUrl ?? noFoundImage,
  );

  useEffect(() => {
    const img = new Image();
    if (!vehicle?.mediaImageUrl) {
      setValidImageUrl(noFoundImage);
      return;
    }
    img.src = vehicle.mediaImageUrl;
    img.onload = () => setValidImageUrl(vehicle.mediaImageUrl!);
    img.onerror = () => setValidImageUrl(noFoundImage);
  }, [vehicle?.mediaImageUrl]);

  return (
    <StyledVehicleCardWrapper
      className={`vehicle-card_wrapper vin-${vehicle.vin}`}
    >
      {isOnlineBuyingLinkModified(vehicle) && (
        <StyledOfferTypeLabelWrapper>
          <StyledBlackContainer className="offer-tag">
            <StyledOfferTypeLabel>
              <TextCustom
                appearance={TextAppearance.headline100}
                tag={TextTag.span}
                style={{
                  [Breakpoints.default]: {
                    fontSize: TextSize.TextSize16,
                    lineHeight: TextLineHeight.TextLineHeight20,
                    color: TextColor.inherit,
                    fontWeight: TextWeight.bold,
                  },
                }}
              >
                <BuildTheDeal iconSize={16} /> {buildMyDeal.onlineSalesLabel}
              </TextCustom>
            </StyledOfferTypeLabel>
          </StyledBlackContainer>
        </StyledOfferTypeLabelWrapper>
      )}
      <div className="top">
        <StyledVehicleImageWrapper
          className="vehicle-card_image"
          imageUrl={validImageUrl}
          placeHolderUrl={AppConstants.PlaceHolderVehicleImage}
          onClick={onHandleVehicleClick}
        >
          <div
            className="image-content"
            aria-label={`image-${vehicle?.model}-${vehicle?.trimLevel}`}
            role="img"
          />
        </StyledVehicleImageWrapper>
      </div>
      <StyledVehicleContentContainer className="vehicle-card_content-container">
        <StyledVehicleContentWrapper className="vehicle-card_vehicle-data">
          <div className="data-wrapper">
            <div className="model-name notranslate">
              <TextCustom {...modelNameTextStyle}>
                {vehicle?.modelYear + ' ' + vehicle?.model}
              </TextCustom>
            </div>
            <div className="model-price">
              <TextCustom {...modelPriceTextStyle}>
                {currencyMap[localeService?.currency] +
                  vehiclePrice?.toLocaleString()}
                <div
                  onClick={onAbortEventReplication}
                  style={{display: 'contents'}}
                >
                  {/* @ts-ignore */}
                  <SupDisclaimer>{priceDisclaimer}</SupDisclaimer>
                </div>
              </TextCustom>
            </div>
            <div className="model-trim notranslate">
              <TextCustom {...modelTrimNameTextStyle}>
                {vehicle?.trimLevel}
              </TextCustom>
            </div>
            <div className="model-mileage">
              <TextCustom {...modelMileageTextStyle}>
                {vehicle.odometer?.toLocaleString()}{' '}
                {resultsSection.vehicleCard.mileage}
              </TextCustom>
            </div>
            <div className="model-warranty">
              <TextCustom {...modelWarrantyTextStyle}>
                {isID4
                  ? ID4_WARRANTY
                  : warrantyMonths[vehicle?.modelYear || ''] ||
                    warrantyMonths['default']}
                {' ' + resultsSection?.vehicleCard.warranty}
              </TextCustom>
              <div
                onClick={onAbortEventReplication}
                style={{display: 'contents'}}
              >
                {/* @ts-ignore */}
                <SupDisclaimer>{warrantyDisclaimer}</SupDisclaimer>
              </div>
            </div>
          </div>
        </StyledVehicleContentWrapper>
        <StyledVehicleDealerWrapper className="vehicle-card_dealer">
          <div>
            <TextCustom {...dealerNameTextStyle}>
              <span className="notranslate">{vehicle?.dealer?.name}</span>
            </TextCustom>
            <TextCustom {...dealerDistanceTextStyle}>
              ({vehicle?.dealer?.distance?.toFixed(2)} {dealer?.distance})
            </TextCustom>
          </div>
          <div className="dealer-phone notranslate">
            <ClickablePhone
              textStyle={dealerPhoneTextStyle}
              phoneNumber={vehicle?.dealer?.phone || ''}
            />
          </div>
          <div className="view-details-cta">
            {/* @ts-ignore */}
            <ButtonCustom
              onClick={onHandleVehicleClick}
              appearance={ButtonAppearance.Secondary}
              stretchContent
            >
              View Details
            </ButtonCustom>
            {isOnlineBuyingLinkModified(vehicle) ? (
              /* @ts-ignore */
              <ButtonCustom
                appearance={ButtonAppearance.Primary}
                onClick={startBuyingHandler.bind(null, 'Build my Deal')}
                stretchContent
              >
                {buildMyDeal.onlineSalesLabel}
              </ButtonCustom>
            ) : null}
          </div>
        </StyledVehicleDealerWrapper>
      </StyledVehicleContentContainer>
    </StyledVehicleCardWrapper>
  );
};

export default SearchResultsVehicleCard;
